/**
 * CLIENT-FINAL screen copy for the W2 employee plan self-selection flow, applied
 * VERBATIM from `data/ss-front-t5/client-screen-copy.md` (client via captain,
 * 2026-07-15). Do not paraphrase — these strings are authoritative. Straight
 * apostrophes are intentional (they match the client doc character-for-character).
 */

/** Screen 1 — Login (the "returning to select plans" variant of `LoginForm`). */
export const LOGIN_COPY = {
  headline: 'Welcome Back!',
  body: `We're glad to see you again. Log in to review your coverage options or make your selections. Your progress is saved, so you can log out and come back whenever you wish.`,
} as const;

/** Screen 2 — Welcome. `Alia` and `Waive Coverage` are rendered bold in-place. */
export const WELCOME_COPY = {
  headline: 'Welcome Back to the OneHealth Plus Benefits Enrollment Platform!',
} as const;

/** Screens 3–8 — per-benefit-category plan pages, keyed by normalized category. */
export interface CategoryCopy {
  headline: string;
  body: string;
  prompt: string;
  waiveLabel: string;
}

export const CATEGORY_COPY: Record<string, CategoryCopy> = {
  Medical: {
    headline: 'Health',
    body: `Welcome to your Plans page! Choosing the right plan is one of the most important steps you can take for your health and your wallet, and we're here to make it easy. Take your time exploring plan details and remember: if you have questions, need to compare plans, or want to run through any "what if" scenarios, our friendly AI assistant Alia is just a click away. She's here to guide you every step of the way!`,
    prompt:
      'Choose who you wish to be covered, or waive coverage for this plan.',
    waiveLabel: 'I waive health coverage',
  },
  Dental: {
    headline: 'Dental',
    body: `Taking care of your smile is more than just brushing and flossing — it's about having the right coverage when you need it. A good dental plan can help you stay on top of your oral health while avoiding unexpected costs.`,
    prompt:
      'Choose who you wish to be covered or waive coverage for this plan.',
    waiveLabel: 'I waive dental coverage',
  },
  Vision: {
    headline: 'Vision',
    body: `Healthy eyes start with regular care. Our vision plan makes it easy and affordable to get the eye exams, glasses, or contacts you need. With access to a large national network of providers and great discounts on frames and lenses, you can keep your vision clear and your costs low. Whether it's routine checkups or updating your prescription, our plans help you see the difference.`,
    prompt:
      'Choose who you wish to be covered, or waive coverage for this plan.',
    waiveLabel: 'I waive vision coverage',
  },
  Accident: {
    headline: 'Accident',
    body: `Accidents happen, but the financial stress doesn't have to. Our accident plan helps cover unexpected expenses from injuries like sprains, fractures, or emergency room visits. Whether it's a weekend mishap or a slip at home, these plans provide cash benefits that can be used however you need — for medical bills, transportation, childcare, or even groceries. It's a smart way to protect your wallet while you focus on getting better.`,
    prompt:
      'Choose who you wish to be covered, or waive coverage for this plan.',
    waiveLabel: 'I waive accident coverage',
  },
  'Critical Illness': {
    headline: 'Critical Illness',
    body: `A serious diagnosis can turn life upside down, but a good plan can help you stay on your feet. Critical Illness coverage offers a lump-sum cash benefit if you're diagnosed with a covered condition like cancer, heart attack, or stroke. You can use the money however you need: medical care, household bills, travel, or recovery support. It's peace of mind during life's toughest moments.`,
    prompt:
      'Choose who you wish to be covered, or waive coverage for this plan.',
    waiveLabel: 'I waive critical illness coverage',
  },
  'Hospital Indemnity': {
    headline: 'Hospital Indemnity',
    body: `A hospital stay can come with more than just a medical bill. That's where the hospital indemnity plan comes in. This plan provides cash benefits for hospital admissions, overnight stays, and related services, paid directly to you. You can use the money for anything from copays and deductibles to everyday expenses. It's an extra layer of financial protection when you need it most.`,
    prompt:
      'Choose who you wish to be covered, or waive coverage for this plan.',
    waiveLabel: 'I waive hospital indemnity coverage',
  },
};

/** Fallback plan-page copy for any category the client sheet didn't enumerate. */
export function categoryCopy(category: string): CategoryCopy {
  return (
    CATEGORY_COPY[category] ?? {
      headline: category,
      body: 'Choose the coverage that’s right for you, or waive this plan.',
      prompt:
        'Choose who you wish to be covered, or waive coverage for this plan.',
      waiveLabel: `I waive ${category.toLowerCase()} coverage`,
    }
  );
}

/**
 * Health-step (Medical) SINGLE-CHOICE screen copy (captain mock, 2026-07-16). The
 * who's-covered question is asked ONCE at the top (You is implicit), then one plan
 * is chosen from the menu below. Unlike the per-plan ancillary categories.
 */
export const MEDICAL_COVERED_PROMPT =
  'Who do you want covered on this plan besides yourself?';
export const MEDICAL_PLAN_SELECT_HEADING = 'Select a health plan';

/**
 * The medical/Health waive certification legalese — shown in an accordion ONLY on
 * the medical plan's waive (other lines waive without certification text).
 */
export const MEDICAL_WAIVE_CERTIFICATION = `I certify that I have been given an opportunity to apply for health coverage and after careful consideration, have decided not to take advantage of this offer. In the event I wish to apply for such coverage hereafter, I may do so, subject to established procedures. If I am declining enrollment for myself or my dependents (including my spouse) because of other health insurance coverage, I may in the future be able to enroll myself or my dependents in this plan, provided that enrollment is requested within the timeframe for timely submissions, noted in the plan document. My dependent(s) or I may be subject to waiting periods specified in the group certificate, if a dependent or I are late enrollees. In addition, if I have a dependent as a result of marriage, birth, adoption or placement for adoption, I may be able to enroll myself and my dependents provided that I request enrollment within the specified timeframe, listed in the plan document after the marriage, birth, adoption or placement of adoption.`;

/** Screen 9 — Quote. */
export const QUOTE_COPY = {
  headline: `Let's review things`,
  body: `Here's a quick look at what you've chosen. Take a moment to confirm everything looks right, then click NEXT to finish up.`,
} as const;

/** Screen 10 — Agreement (the 7-clause TERMS + field title + footer attestation). */
export const AGREEMENT_COPY = {
  headline: 'Agreement',
  intro: 'Please read this section carefully before signing the application:',
  clauses: [
    'I may not assign any payment under this program.',
    'I authorize deduction from my wages/pension, if necessary, for the required premium for the coverage for which I or any dependents have applied.',
    "I am applying for the coverage selected on this application. If I select a coverage not available to me and/or a class for which I am not eligible, I agree that my selection(s) is hereby automatically amended to be consistent with the employer's application.",
    'I am responsible for notifying my employer immediately of any change that would make me or any dependent ineligible for coverage.',
    'I acknowledge that I have read the Significant Terms, Conditions and Authorizations, and I accept such provisions as a condition of coverage. I also acknowledge that the plan fees are deducted pre-tax, requiring the deductions to remain in effect during the plan year, unless there is a change of status as allowed under IRS regulations.',
    'I represent that the answers given to all questions on this application are true and accurate to the best of my knowledge, and I understand that my coverage is contingent on this information. I understand that any misstatements or failure to report new medical information prior to my effective date may result in cancellation of my coverage(s). Any person who, with intent to defraud or knowing that he or she is facilitating fraud against an insurer, submits an application or files a claim containing a false or deceptive statement is guilty of insurance fraud.',
    'I give this authorization for and on behalf of any eligible dependents and myself if covered by the Plan. I am acting as their agent and representative. Read the TERMS section above carefully before signing. Please review your application for errors or omissions.',
  ],
  fieldTitle: 'Type your full name',
  footer:
    'By signing this, I am indicating that I have read and understand the language in the TERMS section of this application and agree to all of its terms.',
} as const;

/** Screen 11 — Final. `{Firstname}` is interpolated. */
export const DONE_COPY = {
  headline: (firstName: string) => `Thank you, ${firstName}!`,
  body: `You're all set! You'll receive an email shortly with instructions on how to access your health portal, view your benefits, and download your ID cards when they're ready. We're excited to have you on board and here to support you every step of the way.`,
} as const;
